import type { RunModifiers } from './run-modifiers';
import type { FactionId } from '../../shared/types';
import type { FactionAffinity } from './faction-affinity';
import { weaponTypeById } from '../progression/boss-weapon-unlocks';

/**
 * 升級種類。Phase 1 只有 'stat' 與 'mutation' 會出現在池中;其餘值預留給後續
 * 階段(夥伴/多重武器/進化),目前不寫任何 gating 邏輯。未標 kind 視為 'stat'。
 */
export type UpgradeKind = 'stat' | 'mutation' | 'companion' | 'weapon' | 'evolution';

/**
 * 升級稀有度(四級)。色相 + 權重的「價值溫度計」,讓 K-3 玩家一眼看出大獎。
 * 視覺契約見 docs/spec/UPGRADE_CHOICE_DESIGN.md §3.1。
 */
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface Upgrade {
  id: string;
  displayName: string;
  emoji: string;
  description: string;
  apply: (mods: RunModifiers) => void;
  /** 稀有度(可選)。未標註時抽選器視為 'common'。 */
  rarity?: Rarity;
  /** 加權抽選的權重(可選)。未標註時抽選器依 rarity 套 RARITY_WEIGHT 預設值。 */
  weight?: number;
  /**
   * 此卡所屬門派(可選)。faction-affinity / flavorPool 用來「加權」(非硬篩)。
   * 未標 = 中立卡。
   */
  faction?: FactionId;
  /**
   * 英雄獨有 (boss 2026-06-17)。標了就「硬限定」:**只有**該門派英雄的三選一會出現這張卡,
   * 其他英雄永遠抽不到。判英雄用 scene `_heroId`(玩家選的英雄門派)。每主角的招牌強化卡 +
   * 召喚主角上場卡走這個欄位。
   */
  exclusiveTo?: FactionId;
  /**
   * 升級種類(可選)。未標視為 'stat'(純數值)。Phase 1 只有 'mutation' 會被
   * scene 實際分流讀取(質變旗標渲染)。
   */
  kind?: UpgradeKind;
  /**
   * 同系列識別(可選),例如 'bounce' / 'explode'。供後續階段做「同系列升級
   * 互斥/疊代」用,Phase 1 不寫邏輯。
   */
  family?: string;
  /** 階級(可選),預留給後續階段的進化 gating,Phase 1 不寫邏輯。 */
  tier?: number;
  /**
   * 商店價(局內金幣,Phase 2)。未標 = 不在商店賣。鐵則:price 是純局內金幣,
   * 與修煉值/freeXP 無關;買 = 扣 coins → 套用同一個 apply(效果大小不變)。
   * K-3 友善:價格用少位數。
   */
  price?: number;
  /**
   * true = 只在商店出現、不進三選一池(Phase 2)。未標 = 兩邊都可出現。
   */
  shopOnly?: boolean;
  /**
   * kind:'weapon' 時指向副武器 registry key(預留 Phase 4)。Phase 2 不寫邏輯。
   */
  weaponKey?: string;
}

/** 取得一張卡的有效 kind(未標視為 'stat')。 */
export function upgradeKind(u: Upgrade): UpgradeKind {
  return u.kind ?? 'stat';
}

/**
 * 各稀有度的預設權重(UPGRADE_CHOICE_DESIGN.md §3.3:common ~60 / rare ~28 / epic ~10 / legendary ~2)。
 * 單張卡若顯式給 weight 則覆蓋此預設。
 */
export const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 60,
  rare: 28,
  epic: 10,
  legendary: 2,
};

/** 稀有度排序值(數字越大越稀有),供「稀有以上」判斷與錯開排序使用。 */
export const RARITY_ORDER: Record<Rarity, number> = {
  common: 0,
  rare: 1,
  epic: 2,
  legendary: 3,
};

export const UPGRADE_POOL: Upgrade[] = [
  // ── 通用數值卡 (未標 faction = 中立,通用權重保底,任何門派都常出) ────────
  {
    id: 'speed-up',
    displayName: '加速',
    emoji: '⚡',
    description: '移動速度 +60%',
    rarity: 'common',
    apply: m => { m.speedMult *= 1.6; },
  },
  {
    id: 'rapid-fire',
    displayName: '連射',
    emoji: '🔫',
    description: '自動射擊 +25% 速度',
    // 平衡 (boss 點名「攻擊增速太強」):削弱 0.55→0.80,且上調 common→rare 降出現率。
    // 配合 RunModifiers.effectiveAutoFireMult() 的下限 clamp,杜絕乘法疊到無敵。
    rarity: 'rare',
    apply: m => { m.autoFireMult *= 0.80; },
  },
  {
    id: 'tank-up',
    displayName: '厚甲',
    emoji: '🛡',
    description: 'HP 上限 +50',
    rarity: 'common',
    apply: m => { m.hpBonus += 50; },
  },
  {
    id: 'heavy-bullet',
    displayName: '重砲',
    emoji: '💥',
    description: '子彈傷害 +10',
    rarity: 'common',
    apply: m => { m.bulletDamageBonus += 10; },
  },
  {
    id: 'big-bullet',
    displayName: '巨彈',
    emoji: '🌀',
    description: '子彈尺寸 ×2 (更易命中)',
    rarity: 'common',
    apply: m => { m.bulletSizeMult *= 2.0; },
  },
  // ── 門派風味卡 (標 faction:affinity 高的門派加權出現,低門不歸零) ──────────
  // 鐵則:faction 只影響「出現機率」,apply 數字與門派無關 (同卡效果一樣)。
  {
    id: 'quick-charge',
    displayName: '快充',
    emoji: '⚙️',
    description: '所有技能充能 +50%',
    rarity: 'rare',
    faction: 'techno', // 機械門 = 速度偵察
    apply: m => { m.chargeMultBonus += 0.5; },
  },
  {
    id: 'spread-plus',
    displayName: '散彈強化',
    emoji: '🎯',
    description: '多 1 顆散彈,但散彈每發傷害較低 ⚠',
    // 平衡:boss 2026-06-13 削 +3→+1、rare→epic;boss 2026-06-17「子彈變多有趣但要有副作用」
    // → 散彈每發只 SPREAD_DAMAGE_FALLOFF× 傷害(scene _spawnBullet),cap 4→3。
    // 散彈定位 = 打更廣/打群,不是乘倍單體 DPS。
    rarity: 'epic',
    faction: 'celestial', // 星算門 = 精準爆發
    apply: m => { m.spreadBulletBonus += 1; },
  },
  {
    id: 'double-loot',
    displayName: '雙倍戰利品',
    emoji: '💰',
    description: '本場勝利 freeXP +100%',
    rarity: 'epic',
    faction: 'dominion', // 疆域門 = 資源支援
    apply: m => { m.freeXPMult *= 2.0; },
  },
  {
    id: 'crit',
    displayName: '暴擊',
    emoji: '🌟',
    description: '子彈 35% 機率 2x 傷害',
    rarity: 'rare',
    faction: 'celestial', // 星算門 = 精準爆發
    apply: m => { m.critChance += 0.35; m.critMult = 2; },
  },
  {
    id: 'starter-shield',
    displayName: '起手護盾',
    emoji: '🔵',
    description: '立即獲得 2 點護盾',
    rarity: 'rare',
    faction: 'runic', // 符文門 = 防禦控制
    apply: m => { m.startShieldHits += 2; },
  },
  {
    id: 'lifesteal',
    displayName: '吸血',
    emoji: '💚',
    description: '每殺 5 隻敵人回 2 HP',
    rarity: 'rare',
    faction: 'elemental', // 自然門 = 元素持續
    apply: m => { m.lifestealPerKill += 0.4; },
  },
  {
    id: 'slow-aura',
    displayName: '冰光環',
    emoji: '❄️',
    description: '連 5 殺後敵人移速 -30% 持續 5s',
    rarity: 'rare',
    faction: 'runic', // 符文門 = 防禦控制
    apply: m => { m.slowAuraEnabled = true; },
  },
  // ── 主軸風味卡補齊 (CHARACTER_SYSTEM_DESIGN §3 + §5.1) ──────────────────────
  // 鐵則:faction 只影響「出現機率」(flavorPool 加權),apply 數字全英雄一致;
  // 絕不新增 RunModifiers 欄位、絕不碰 progress/factionXP/freeXP/skillLevels。
  // 每門派主軸卡補到 ≥3,稀有度刻意分散 (common/rare/epic)。

  // 星算俠 celestial:爆發 → 散彈 / 暴擊 / 連射 / 巨彈
  {
    id: 'celestial-auto',
    displayName: '星算連射',
    emoji: '✨',
    description: '自動射擊 +18% 速度',
    // 平衡:射速卡削弱 0.7→0.85,並上調 common→rare 降出現率 (boss 點名)。
    rarity: 'rare',
    faction: 'celestial',
    apply: m => { m.autoFireMult *= 0.85; },
  },
  {
    id: 'celestial-bigshot',
    displayName: '星爆巨彈',
    emoji: '☄️',
    description: '子彈尺寸 ×1.6 (更易命中)',
    rarity: 'rare',
    faction: 'celestial',
    apply: m => { m.bulletSizeMult *= 1.6; },
  },
  {
    id: 'celestial-supercrit',
    displayName: '超新星暴擊',
    emoji: '💫',
    description: '暴擊機率 +20%,暴擊變 3 倍傷害',
    rarity: 'epic',
    faction: 'celestial',
    apply: m => { m.critChance += 0.2; m.critMult = 3; },
  },

  // 符文守 runic:護盾控場 → 起手護盾 / 冰光環 / 厚甲
  {
    id: 'runic-armor',
    displayName: '符文厚甲',
    emoji: '🛡',
    description: 'HP 上限 +40',
    rarity: 'common',
    faction: 'runic',
    apply: m => { m.hpBonus += 40; },
  },
  {
    id: 'runic-bigshield',
    displayName: '大符文盾',
    emoji: '🔷',
    description: '立即獲得 3 點護盾',
    rarity: 'epic',
    faction: 'runic',
    apply: m => { m.startShieldHits += 3; },
  },

  // 機械斥 techno:提速穿透 → 移速 / 連射 / 快充
  {
    id: 'techno-boost',
    displayName: '機械加速',
    emoji: '🏃',
    description: '移動速度 +40%',
    rarity: 'common',
    faction: 'techno',
    apply: m => { m.speedMult *= 1.4; },
  },
  {
    id: 'techno-rapid',
    displayName: '機械連射',
    emoji: '🤖',
    description: '自動射擊 +18% 速度',
    // 平衡:射速卡削弱 0.65→0.85 (boss 點名「攻擊增速太強」),已是 rare 不再上調。
    rarity: 'rare',
    faction: 'techno',
    apply: m => { m.autoFireMult *= 0.85; },
  },

  // 自然御 elemental:鋪場燃燒 → 吸血 / 爆炸彈 / 巨彈
  {
    id: 'elemental-bigshot',
    displayName: '元素巨彈',
    emoji: '🌿',
    description: '子彈尺寸 ×1.8 (更易命中)',
    rarity: 'common',
    faction: 'elemental',
    apply: m => { m.bulletSizeMult *= 1.8; },
  },
  {
    id: 'elemental-blast',
    displayName: '火焰爆裂',
    emoji: '🔥',
    description: '打中會爆!範圍 +30',
    rarity: 'epic',
    kind: 'mutation',
    family: 'explode',
    faction: 'elemental',
    apply: m => { m.explodeOnHitRadius += 30; },
  },

  // 疆域王 dominion:範圍清場 → 厚甲 / 重砲 / 金幣
  {
    id: 'dominion-armor',
    displayName: '疆域重甲',
    emoji: '🏰',
    description: 'HP 上限 +60',
    rarity: 'common',
    faction: 'dominion',
    apply: m => { m.hpBonus += 60; },
  },
  {
    id: 'dominion-cannon',
    displayName: '疆域巨砲',
    emoji: '🎆',
    description: '子彈傷害 +12',
    rarity: 'rare',
    faction: 'dominion',
    apply: m => { m.bulletDamageBonus += 12; },
  },
  {
    id: 'dominion-gold',
    displayName: '金幣大豐收',
    emoji: '💰',
    description: '金幣掉落 ×1.5',
    rarity: 'rare',
    faction: 'dominion',
    apply: m => { m.coinDropMult *= 1.5; },
  },

  // ── 質變卡 (mutation) ─────────────────────────────────────────────────────
  // apply 只動純資料(寫旗標進 RunModifiers);實際反彈/爆炸由 scene 每次碰撞
  // 讀旗標施工(沿用 slowAuraEnabled 的同模式)。
  {
    id: 'bounce-shot',
    displayName: '彈跳彈',
    emoji: '🪀',
    description: '子彈會彈!',
    rarity: 'epic',
    kind: 'mutation',
    family: 'bounce',
    apply: m => { m.bounceCount += 2; },
  },
  {
    id: 'explode-shot',
    displayName: '爆炸彈',
    emoji: '💣',
    description: '打中會爆!',
    rarity: 'epic',
    kind: 'mutation',
    family: 'explode',
    apply: m => { m.explodeOnHitRadius += 40; },
  },

  // ── 通用多樣化卡 (boss 2026-06-17「太多三選一都是子彈變多/變大」→ 補非子彈系 archetype) ──
  // 全中立(任何英雄都常出),用既有 RunModifiers 欄位(scene 已接線:pierceCount=子彈穿透、
  // lifestealPerKill=擊殺回血、coinDropMult=金幣),不新增欄位、不碰 progress/修煉值。
  {
    id: 'pierce-shot',
    displayName: '穿透彈',
    emoji: '🏹',
    description: '子彈穿透 1 個敵人續飛(穿一整排)',
    rarity: 'rare',
    apply: m => { m.pierceCount += 1; },
  },
  {
    id: 'vampire',
    displayName: '嗜血',
    emoji: '🩸',
    description: '擊殺敵人回血(可疊加)',
    rarity: 'rare',
    apply: m => { m.lifestealPerKill += 0.5; },
  },
  {
    id: 'scavenge',
    displayName: '磁能拾荒',
    emoji: '🧲',
    description: '金幣掉落 ×1.4',
    rarity: 'common',
    apply: m => { m.coinDropMult *= 1.4; },
  },

  // ── 僚機 / 近戰 (companion / weapon) ───────────────────────────────────────
  // apply 只把武器 key 推進 mods.autoWeapons (純局內資料,上限 3、去重);實際
  // 生僚機/鐮刀由 scene 每幀讀 autoWeapons 施工 (同 slowAuraEnabled 旗標模式)。
  // 鐵則:只動 RunModifiers,不碰 progress/factionXP/freeXP/skillLevels。
  {
    id: 'companion-drone',
    displayName: '偵察僚機',
    emoji: '🛸',
    description: '召喚小飛機幫你打',
    rarity: 'epic',
    kind: 'companion',
    faction: 'techno', // 機械門 = 速度偵察
    apply: m => { if (!m.autoWeapons.includes('drone') && m.autoWeapons.length < 3) m.autoWeapons.push('drone'); },
  },
  {
    id: 'weapon-scythe',
    displayName: '元素旋刃',
    emoji: '🌀',
    description: '近身敵人被切',
    rarity: 'epic',
    kind: 'weapon',
    faction: 'elemental', // 自然門 = 元素持續
    apply: m => { if (!m.autoWeapons.includes('scythe') && m.autoWeapons.length < 3) m.autoWeapons.push('scythe'); },
  },

  // ── 英雄獨有卡 (boss 2026-06-17):每主角專屬,只在該英雄的三選一出現 ─────────────
  // 每主角 2 張:① 召喚主角立繪上場助戰(autoWeapons 'hero',複用僚機系統)②招牌強化
  // (套該門派定位:星算爆發/符文防控/機械速度/自然持續/疆域資源)。各帶專屬特效(scene)。
  // 純局內(RunModifiers,跨場歸零),不碰 progress/factionXP。
  { id: 'summon-hero-celestial', displayName: '召喚 星算英雄', emoji: '⚡🦸', description: '星算英雄登場助戰,自動精準射擊',
    rarity: 'legendary', exclusiveTo: 'celestial', faction: 'celestial',
    apply: m => { if (!m.autoWeapons.includes('hero') && m.autoWeapons.length < 3) m.autoWeapons.push('hero'); } },
  { id: 'summon-hero-runic', displayName: '召喚 符文英雄', emoji: '🔮🦸', description: '符文英雄登場助戰,自動射擊',
    rarity: 'legendary', exclusiveTo: 'runic', faction: 'runic',
    apply: m => { if (!m.autoWeapons.includes('hero') && m.autoWeapons.length < 3) m.autoWeapons.push('hero'); } },
  { id: 'summon-hero-techno', displayName: '召喚 機械英雄', emoji: '🤖🦸', description: '機械英雄登場助戰,自動快射',
    rarity: 'legendary', exclusiveTo: 'techno', faction: 'techno',
    apply: m => { if (!m.autoWeapons.includes('hero') && m.autoWeapons.length < 3) m.autoWeapons.push('hero'); } },
  { id: 'summon-hero-elemental', displayName: '召喚 自然英雄', emoji: '🌀🦸', description: '自然英雄登場助戰,自動射擊',
    rarity: 'legendary', exclusiveTo: 'elemental', faction: 'elemental',
    apply: m => { if (!m.autoWeapons.includes('hero') && m.autoWeapons.length < 3) m.autoWeapons.push('hero'); } },
  { id: 'summon-hero-dominion', displayName: '召喚 疆域英雄', emoji: '🏰🦸', description: '疆域英雄登場助戰,自動射擊',
    rarity: 'legendary', exclusiveTo: 'dominion', faction: 'dominion',
    apply: m => { if (!m.autoWeapons.includes('hero') && m.autoWeapons.length < 3) m.autoWeapons.push('hero'); } },

  // 招牌強化(各門派定位,exclusiveTo 硬限定)
  { id: 'sig-celestial', displayName: '星算·精準爆發', emoji: '⚡', description: '暴擊率 +30%,暴擊傷害大增',
    rarity: 'epic', exclusiveTo: 'celestial', faction: 'celestial',
    apply: m => { m.critChance += 0.3; m.critMult = Math.max(m.critMult, 3); } },
  { id: 'sig-runic', displayName: '符文·壁壘控場', emoji: '🔮', description: '開場護盾 +3,並啟動減速光環',
    rarity: 'epic', exclusiveTo: 'runic', faction: 'runic',
    apply: m => { m.startShieldHits += 3; m.slowAuraEnabled = true; } },
  { id: 'sig-techno', displayName: '機械·超頻速射', emoji: '🤖', description: '射速大幅提升',
    rarity: 'epic', exclusiveTo: 'techno', faction: 'techno',
    apply: m => { m.autoFireMult *= 0.7; } },
  { id: 'sig-elemental', displayName: '自然·共生持續', emoji: '🌀', description: '擊殺回血,命中附帶範圍灼燒',
    rarity: 'epic', exclusiveTo: 'elemental', faction: 'elemental',
    apply: m => { m.lifestealPerKill += 0.5; m.explodeOnHitRadius = Math.max(m.explodeOnHitRadius, 36); } },
  { id: 'sig-dominion', displayName: '疆域·資源徵集', emoji: '🏰', description: '金幣 ×2,生命上限大增',
    rarity: 'epic', exclusiveTo: 'dominion', faction: 'dominion',
    apply: m => { m.coinDropMult *= 2; m.hpBonus += 60; } },

  // 騎乘 (boss 2026-06-17):英雄「坐上戰車」(autoWeapons 'hero-mount' → scene _updateCompanions
  // mounted 分支:貼身隨車 + 週期招牌射擊)+ 門派被動(既有 RunModifiers 欄位,已接線)。
  // exclusiveTo 硬限該英雄。純局內、跨場歸零,不碰 progress/factionXP。跟「召喚」差別=貼身強化戰車本體。
  { id: 'mount-hero-celestial', displayName: '星算·騎乘', emoji: '⚡🏇', description: '星算英雄坐上戰車:暴擊↑ + 招牌精準射擊',
    rarity: 'legendary', exclusiveTo: 'celestial', faction: 'celestial',
    apply: m => { m.critChance += 0.15; m.critMult = Math.max(m.critMult, 2); if (!m.autoWeapons.includes('hero-mount') && m.autoWeapons.length < 3) m.autoWeapons.push('hero-mount'); } },
  { id: 'mount-hero-runic', displayName: '符文·騎乘', emoji: '🔮🏇', description: '符文英雄坐上戰車:開場護盾 +2 + 招牌射擊',
    rarity: 'legendary', exclusiveTo: 'runic', faction: 'runic',
    apply: m => { m.startShieldHits += 2; if (!m.autoWeapons.includes('hero-mount') && m.autoWeapons.length < 3) m.autoWeapons.push('hero-mount'); } },
  { id: 'mount-hero-techno', displayName: '機械·騎乘', emoji: '🤖🏇', description: '機械英雄坐上戰車:移速↑ + 招牌快射',
    rarity: 'legendary', exclusiveTo: 'techno', faction: 'techno',
    apply: m => { m.speedMult *= 1.25; if (!m.autoWeapons.includes('hero-mount') && m.autoWeapons.length < 3) m.autoWeapons.push('hero-mount'); } },
  { id: 'mount-hero-elemental', displayName: '自然·騎乘', emoji: '🌀🏇', description: '自然英雄坐上戰車:擊殺回血 + 招牌射擊',
    rarity: 'legendary', exclusiveTo: 'elemental', faction: 'elemental',
    apply: m => { m.lifestealPerKill += 0.4; if (!m.autoWeapons.includes('hero-mount') && m.autoWeapons.length < 3) m.autoWeapons.push('hero-mount'); } },
  { id: 'mount-hero-dominion', displayName: '疆域·騎乘', emoji: '🏰🏇', description: '疆域英雄坐上戰車:HP↑ + 招牌射擊',
    rarity: 'legendary', exclusiveTo: 'dominion', faction: 'dominion',
    apply: m => { m.hpBonus += 60; if (!m.autoWeapons.includes('hero-mount') && m.autoWeapons.length < 3) m.autoWeapons.push('hero-mount'); } },
];

/**
 * 英雄獨有卡硬篩 (boss 2026-06-17):移除「不屬於此英雄」的 exclusiveTo 卡。
 * 中立卡(無 exclusiveTo)恆保留;有 exclusiveTo 的只在 === heroId 時保留。純函式可單測。
 */
export function filterExclusiveForHero(pool: Upgrade[], heroId: FactionId): Upgrade[] {
  return pool.filter((u) => !u.exclusiveTo || u.exclusiveTo === heroId);
}

/** 取得一張卡的有效稀有度(未標註預設 common)。 */
export function upgradeRarity(u: Upgrade): Rarity {
  return u.rarity ?? 'common';
}

/** 取得一張卡的有效抽選權重(顯式 weight 優先,否則依稀有度套預設)。 */
export function upgradeWeight(u: Upgrade): number {
  if (typeof u.weight === 'number' && u.weight > 0) return u.weight;
  return RARITY_WEIGHT[upgradeRarity(u)];
}

/** 「稀有以上」= rare / epic / legendary。 */
export function isRareOrAbove(u: Upgrade): boolean {
  return RARITY_ORDER[upgradeRarity(u)] >= RARITY_ORDER.rare;
}

/**
 * 從候選清單依權重抽 1 張,回傳其索引。
 * rand() 須回傳 [0,1)。空清單回傳 -1。
 */
function weightedPickIndex(pool: Upgrade[], rand: () => number): number {
  if (pool.length === 0) return -1;
  let total = 0;
  for (const u of pool) total += upgradeWeight(u);
  let r = rand() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= upgradeWeight(pool[i]);
    if (r < 0) return i;
  }
  return pool.length - 1; // 浮點數保險
}

/**
 * 加權抽 n 張不重複的升級。
 * - 依 weight 加權(common~60/rare~28/epic~10/legendary~2)。
 * - forceRareFirst=true 時,第一張強制從「稀有以上」池中抽(保底用)。
 * - 盡量讓抽出的卡稀有度錯開:第二張起,若還有「尚未出現過的稀有度」卡片,
 *   優先只從那些卡片抽,讓三張不易同級(spec §3.3「三張稀有度盡量錯開」)。
 */
function weightedPickN(
  n: number,
  rand: () => number,
  forceRareFirst: boolean,
  source: Upgrade[] = UPGRADE_POOL,
): Upgrade[] {
  const remaining = [...source];
  const result: Upgrade[] = [];
  const pickedRarities = new Set<Rarity>();

  for (let k = 0; k < n && remaining.length > 0; k++) {
    let candidates = remaining;

    // 第一張保底:只從稀有以上抽(若池中有的話)
    if (k === 0 && forceRareFirst) {
      const rareUp = remaining.filter(isRareOrAbove);
      if (rareUp.length > 0) candidates = rareUp;
    }

    // 錯開:若已抽過至少一張,且仍有「尚未出現過的稀有度」卡片,優先從那些抽,製造分級對比。
    if (pickedRarities.size > 0) {
      const freshRarity = candidates.filter(u => !pickedRarities.has(upgradeRarity(u)));
      if (freshRarity.length > 0) candidates = freshRarity;
    }

    const idxInCandidates = weightedPickIndex(candidates, rand);
    if (idxInCandidates < 0) break;
    const chosen = candidates[idxInCandidates];

    result.push(chosen);
    pickedRarities.add(upgradeRarity(chosen));
    // 從 remaining 移除(確保不重複)
    const ri = remaining.indexOf(chosen);
    if (ri >= 0) remaining.splice(ri, 1);
  }

  return result;
}

/**
 * 無狀態加權抽 3 張(向後相容舊呼叫點 `pickThree()`)。
 * 簽章不變:(rand?) => Upgrade[]。保底由 createUpgradePicker() 的有狀態版本提供。
 */
export function pickThree(rand: () => number = Math.random): Upgrade[] {
  return weightedPickN(3, rand, false);
}

/** 有狀態抽選器,內含保底計數。 */
export interface UpgradePicker {
  /**
   * 抽 3 張,套用加權 + 保底 + 稀有度錯開。
   * @param rand   注入的亂數來源(測試用),預設 Math.random。
   * @param source 可選的候選池(測試用),預設 UPGRADE_POOL。
   */
  pick: (rand?: () => number, source?: Upgrade[]) => Upgrade[];
  /** 目前連續未出現「稀有以上」的次數(測試/除錯用)。 */
  readonly pityCounter: number;
}

/** 保底門檻:連續這麼多次抽選都沒「稀有以上」→ 下一次強制至少 1 張稀有。 */
export const PITY_THRESHOLD = 4;

/**
 * 建立帶保底狀態的抽選器(spec §3.3)。
 *
 * 用法(接線 agent 在 scene 改用):
 *   const picker = createUpgradePicker();   // 每場戰鬥建一個,保留整場狀態
 *   const choices = picker.pick();          // 取代原本的 pickThree()
 */
export function createUpgradePicker(): UpgradePicker {
  let pity = 0;

  return {
    pick(rand: () => number = Math.random, source: Upgrade[] = UPGRADE_POOL): Upgrade[] {
      const forceRare = pity >= PITY_THRESHOLD;
      const picks = weightedPickN(3, rand, forceRare, source);
      const gotRare = picks.some(isRareOrAbove);
      pity = gotRare ? 0 : pity + 1;
      return picks;
    },
    get pityCounter() {
      return pity;
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2 — 門派風味過濾 (flavorPool) §4-0
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 主修門派風味卡的「加權上限倍率」。affinity=1 的門派,其風味卡權重最多 ×2.5,
 * 讓玩家明顯更常抽到自己門派的卡;通用卡 (未標 faction) 權重恆不變 (保底)。
 */
export const FLAVOR_MAX_BOOST = 2.5;

/**
 * 依門派親和度對候選池「加權」(不過濾掉任何卡,確保池永遠 ≥ source 大小、≥3 可抽)。
 *
 * 規則 (§4-0):
 *   - 通用卡 (無 faction):權重不變 (保底,永遠在池)。
 *   - 風味卡 (有 faction):權重 ×= 1 + (FLAVOR_MAX_BOOST-1) × affinity[faction]。
 *     affinity 高 (主修門) → 接近 ×FLAVOR_MAX_BOOST;affinity 低 (未練門,
 *     仍有 AFFINITY_FLOOR=0.2) → 仍 >1,**不歸零** (低權但抽得到)。
 *
 * 回傳「淺拷貝 + 覆寫 weight」的新卡陣列 (不改原 UPGRADE_POOL)。產物直接當
 * `createUpgradePicker().pick(rand, source)` 的 source。
 *
 * 鐵則:只改 weight (出現機率),apply / 數值欄位原封不動 (效果大小與門派無關)。
 *
 * @param pool     候選池 (預設 UPGRADE_POOL;只取「非 shopOnly」的卡進三選一)。
 * @param affinity computeFactionAffinity 產的唯讀快照。
 */
export function flavorPool(
  pool: Upgrade[] = UPGRADE_POOL,
  affinity: FactionAffinity,
): Upgrade[] {
  return pool
    .filter(u => !u.shopOnly) // shopOnly 卡只在商店,不進三選一。
    .map(u => {
      if (!u.faction) return u; // 通用卡:權重不變,直接沿用原物件。
      const aff = affinity[u.faction] ?? 0;
      const boost = 1 + (FLAVOR_MAX_BOOST - 1) * Math.max(0, Math.min(1, aff));
      const baseW = upgradeWeight(u);
      // 淺拷貝 + 覆寫 weight,絕不動 apply / rarity / 數值欄位。
      return { ...u, weight: baseW * boost };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// 廣/高傷二擇一 — build 原型互斥稀釋 (boss 2026-06-14)
// ─────────────────────────────────────────────────────────────────────────────
//
// boss 2026-06-14:「不能 打得又廣傷害又高」。設計原則:廣(寬度/穿透/多目標)與
// 高單發傷害二擇一,不可兼得。除了「命中傷害衰減」(讓廣命中後段傷遞減)外,這裡從
// 「升級牌池」側補一道:當一場已重押某原型(廣 or 高傷),就稀釋「對立原型」的卡權重,
// 讓單一 build 不易同時拿滿「最大寬度 + 最大單發傷害」。純改 weight(出現機率),
// 不改 apply / 數值,不碰學習軌。

/** 卡的「原型」:width=廣(寬度/穿透/多目標)、damage=高單發傷害、neutral=不影響此軸。 */
export type BuildArchetype = 'width' | 'damage' | 'neutral';

/**
 * 一張升級卡屬於哪個原型(用 id 對照,純資料)。只標「會推升某一軸到頂」的卡:
 *   - width:散彈/巨彈(命中面變廣 + 更易同時打多隻)。
 *   - damage:重砲/暴擊(拉高單發傷害)。
 * 其餘(移速/HP/金幣/吸血/光環/僚機…)= neutral,不參與互斥(它們不會造成「又廣又高」)。
 */
export function buildArchetypeOf(u: Upgrade): BuildArchetype {
  switch (u.id) {
    // 廣:多顆 / 大判定 → 同時打更多敵人。
    case 'spread-plus':
    case 'big-bullet':
    case 'celestial-bigshot':
    case 'elemental-bigshot':
      return 'width';
    // 高傷:拉高單發/單 tick 傷害。
    case 'heavy-bullet':
    case 'dominion-cannon':
    case 'crit':
    case 'celestial-supercrit':
      return 'damage';
    default:
      return 'neutral';
  }
}

/**
 * 一場「已投資程度」門檻:同一原型的卡被選 ≥ 此次數,就視為「已重押這個原型」,
 * 開始稀釋對立原型的卡。設 2 = 拿了 2 張同軸卡才觸發(避免第一張就鎖死選擇,K-3 友善)。
 */
export const ARCHETYPE_LOCK_THRESHOLD = 2;

/** 觸發互斥後,對立原型卡的權重稀釋倍率(0.25 = 砍到 1/4,仍 >0 抽得到,不硬鎖)。 */
export const OPPOSITE_ARCHETYPE_DILUTION = 0.25;

/**
 * 依「本場已選的原型投資」稀釋對立原型卡的權重(廣/高傷二擇一)。
 *
 * 規則:
 *   - 數本場已選的 width / damage 卡張數(pickCounts × buildArchetypeOf)。
 *   - 若某一軸已 ≥ ARCHETYPE_LOCK_THRESHOLD → 對「另一軸」的卡權重 ×OPPOSITE_ARCHETYPE_DILUTION。
 *   - 兩軸都重押(極端) → 兩邊互相稀釋(雙向),自然收斂回中庸。
 *   - neutral 卡與「尚未重押任何軸」時:權重原封不動(行為同今日)。
 *
 * 回「淺拷貝 + 覆寫 weight」的新陣列(不改原物件 / apply / 數值)。可單測(純函式)。
 *
 * @param pool       候選池(通常是 flavorPool 的產物)。
 * @param pickCounts 本場每張卡被選次數(RunModifiers.pickCounts)。
 */
export function dilateByBuildArchetype(
  pool: Upgrade[],
  pickCounts: Readonly<Record<string, number>>,
): Upgrade[] {
  let widthPicks = 0;
  let damagePicks = 0;
  for (const [id, n] of Object.entries(pickCounts)) {
    if (!(n > 0)) continue;
    // 用該卡的原型 ×次數累計。找不到卡定義就略過(資料一致性保險)。
    const arch = archetypeById(id);
    if (arch === 'width') widthPicks += n;
    else if (arch === 'damage') damagePicks += n;
  }
  const widthLocked = widthPicks >= ARCHETYPE_LOCK_THRESHOLD;
  const damageLocked = damagePicks >= ARCHETYPE_LOCK_THRESHOLD;
  if (!widthLocked && !damageLocked) return pool; // 沒重押任何軸 → 不動,行為同今日。

  return pool.map(u => {
    const arch = buildArchetypeOf(u);
    // 已重押「廣」→ 稀釋「高傷」卡;已重押「高傷」→ 稀釋「廣」卡。
    const dilute =
      (widthLocked && arch === 'damage') || (damageLocked && arch === 'width');
    if (!dilute) return u;
    const baseW = upgradeWeight(u);
    return { ...u, weight: baseW * OPPOSITE_ARCHETYPE_DILUTION };
  });
}

/** 由卡 id 反查原型(在 UPGRADE_POOL 找定義 → buildArchetypeOf)。找不到 → neutral。 */
function archetypeById(id: string): BuildArchetype {
  const u = UPGRADE_POOL.find(c => c.id === id);
  return u ? buildArchetypeOf(u) : 'neutral';
}

// ─────────────────────────────────────────────────────────────────────────────
// 打贏魔王解鎖武器類型 — 場內牌池 gating
// ─────────────────────────────────────────────────────────────────────────────
//
// 鐵則(學習/遊戲分離,CLAUDE.md / PROGRESSION_AND_ACCOUNTS.md §7):
//   - 武器類型解鎖 = 玩遊戲軌「廣度」(progress.unlockedWeapons),只決定「這張卡
//     會不會出現在三選一池」;**未解鎖不進池**。
//   - 解鎖後也得在 run 中抽到才生效;效果走單場 RunModifiers(WeaponType.applyRunMod
//     複用既有 spreadBulletBonus/bounceCount/pierceCount/spreadBlastRadius/
//     explodeOnHitRadius/bulletSizeMult 旗標),跨場 `new RunModifiers()` 歸零。
//     絕不碰 progress/factionXP/freeXP/skillLevels。
//   - 各武器是 sidegrade(不同彈道/玩法,整體強度相當),解鎖不讓「同一關更易打贏」。

/**
 * 把「已解鎖的武器類型 id」轉成可進三選一池的 Upgrade 卡。
 * 未解鎖的武器 → 不產卡(自動 gating)。卡的 `apply` 直接呼叫 WeaponType.applyRunMod
 * (只寫單場旗標)。rarity 標 'epic'(武器= run 中的大獎,但效果與既有 epic 卡相當)。
 *
 * @param unlockedWeapons progress.unlockedWeapons(玩遊戲軌)。空 → 回 []。
 */
export function weaponCardsFor(unlockedWeapons: readonly string[] | undefined): Upgrade[] {
  if (!unlockedWeapons || unlockedWeapons.length === 0) return [];
  const out: Upgrade[] = [];
  for (const id of unlockedWeapons) {
    const w = weaponTypeById(id);
    if (!w) continue; // 未知 id(資料不一致)→ 跳過,不污染池。
    out.push({
      id: w.id,
      displayName: w.displayName,
      emoji: w.emoji,
      description: w.description,
      rarity: 'epic',
      kind: 'weapon',
      apply: (m) => { w.applyRunMod(m); },
    });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2 — 局內金幣商店 (shop) §4-1
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 商店商品 = registry 內標 `price` 的 Upgrade。新增商品 = 加一筆資料 (這裡)。
 * 鐵則:price 是純局內金幣;買 = 扣 coins → 套用同一個 apply (效果不變)。
 * K-3 友善:價格少位數 (5/8/12...)。商品復用既有卡的 apply,效果與三選一一致。
 */
export const SHOP_CATALOG: Upgrade[] = [
  { id: 'shop-heal',     displayName: '補血',   emoji: '❤️', description: 'HP 上限 +50', rarity: 'common', price: 5,  shopOnly: true, apply: m => { m.hpBonus += 50; } },
  { id: 'shop-rapid',    displayName: '連射',   emoji: '🔫', description: '自動射擊更快', rarity: 'common', price: 8,  apply: m => { m.autoFireMult *= 0.85; } }, // 平衡:射速削弱 0.7→0.85 (與三選一射速卡一致)。
  { id: 'shop-damage',   displayName: '重砲',   emoji: '💥', description: '子彈傷害 +10', rarity: 'common', price: 8,  apply: m => { m.bulletDamageBonus += 10; } },
  { id: 'shop-crit',     displayName: '暴擊',   emoji: '🌟', description: '暴擊機率 +25%', rarity: 'rare',   price: 12, apply: m => { m.critChance += 0.25; m.critMult = 2; } },
  { id: 'shop-bounce',   displayName: '彈跳彈', emoji: '🪀', description: '子彈會彈!',   rarity: 'epic',   price: 15, kind: 'mutation', family: 'bounce', apply: m => { m.bounceCount += 2; } },
];

/**
 * 從商店目錄挑 n 件商品 (預設 4)。純函式、可重現 (注入 rand)。不做刷新/議價。
 * gating (買得起才能買) 由 UI/scene 讀 mods.coins 判斷,不在此處。
 *
 * @param n       商品數 (K-3 友善:3~4 件)。
 * @param rand    [0,1) 亂數來源。
 * @param catalog 來源目錄 (測試用),預設 SHOP_CATALOG。
 */
export function shopOffer(
  n = 4,
  rand: () => number = Math.random,
  catalog: Upgrade[] = SHOP_CATALOG,
): Upgrade[] {
  const remaining = catalog.filter(u => typeof u.price === 'number' && u.price > 0);
  const out: Upgrade[] = [];
  const count = Math.min(n, remaining.length);
  for (let k = 0; k < count; k++) {
    const i = Math.floor(rand() * remaining.length) % remaining.length;
    out.push(remaining[i]);
    remaining.splice(i, 1);
  }
  return out;
}
